import React, {useEffect, useState} from 'react';
import MainLayout from '../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {ScreenProps} from '../../../../interfaces/screens/mainScreen';
import TextGeneric from '../../../../components/textGeneric';
import GenericButton from '../../../../components/button';
import {tr} from '../../../../translations';
import {isWeb} from '../../../../utils/responsive';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import screens from '../../../../config/screens';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import Icon from '../../../../components/icon';
import {Theme} from '../../../../theme/lightTheme';
import {PatientType} from '../../../../interfaces/api/content/ehr';
import {deletePatient, getPatients} from './actions';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  View,
} from 'react-native';
import TableRowSkeleton from '../../../../components/tableRowSkeleton';
import SearchListSkeleton from '../../../../components/searchListSkeleton';
import Loader from '../../../../components/loader';
import {openQuestionModal} from '../../../../connected-components/questionModal/actions';
import EmptyList from '../../../../components/emptyList';
import {EmptyContainer} from '../../home';
import dayjs from 'dayjs';

const Container = styled.View<{headerHeight: number}>`
  width: 100%;
  flex: 1;
  position: relative;
  padding-top: ${({theme, headerHeight}) => headerHeight + theme.text.s3}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({theme}) => theme.text.s55}px;
`;

const HeaderContainer = styled.View`
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({theme}) => theme.text.s3}px;
`;

const Title = styled(TextGeneric)`
  font-size: 24px;
  color: ${({theme}) => theme.text.title};
`;

const AddButton = styled(GenericButton)`
  max-width: 200px;
`;

const TableContainer = styled.View`
  width: 100%;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 10px;
  overflow: hidden;
  shadow-opacity: 0.1;
  shadow-radius: 10px;
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 0px 2px;
  elevation: 2;
`;

const TableHeader = styled.View`
  flex-direction: row;
  background-color: ${({theme}) => theme.patients.headerBackground};
  padding-vertical: ${({theme}) => theme.text.s55}px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
`;

const TableBody = styled.ScrollView`
  width: 100%;
`;

const TableHeaderCell = styled.View<{width: string}>`
  width: ${({width}) => width};
  padding-horizontal: ${({theme}) => theme.text.s55}px;
`;

const TableHeaderText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  text-transform: uppercase;
`;

const TableRow = styled.View`
  flex-direction: row;
  padding-vertical: ${({theme}) => theme.text.s55}px;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.border};
`;

const TableCell = styled.View<{width: string}>`
  width: ${({width}) => width};
  padding-horizontal: ${({theme}) => theme.text.s55}px;
  justify-content: center;
`;

const CellText = styled(TextGeneric)`
  font-size: 14px;
  color: ${({theme}) => theme.text.dark};
`;

const IconContainer = styled.TouchableOpacity`
  padding: ${({theme}) => theme.text.s6 / 2}px;
  background-color: ${({theme}) => theme.settings.icon + '11'};
  border-radius: 4px;
  margin-horizontal: ${({theme}) => theme.text.s12 / 4}px;
`;

const ActionButtonsContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

// Card view for mobile
const CardContainer = styled.View`
  width: 100%;
`;

const PatientCard = styled.View`
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 10px;
  padding: ${({theme}) => theme.text.s55}px;
  margin-bottom: 12px;
  shadow-opacity: 0.1;
  shadow-radius: 5px;
  shadow-color: ${({theme}) => theme.text.black};
  shadow-offset: 0px 2px;
  elevation: 2;
`;

const CardRow = styled.View`
  flex-direction: row;
  margin-bottom: 6px;
`;

const CardLabel = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  width: 100px;
`;

const CardValue = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  flex: 1;
`;

const loadMoreThreshold = 0.75; // Trigger when 75% scrolled

/**
 * Patients screen component.
 *
 * @param {any} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const Patients: React.FC<ScreenProps> = () => {
  const navigation = useNavigation<NavigationProp<any>>();
  const headerHeight = useCustomHeaderHeight();
  const {isMobile} = useResponsiveDimensions();
  const theme = useTheme() as Theme;
  const [data, setData] = useState<PatientType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);

  const loadMore = async (refresh = false) => {
    if (isLoading && !refresh) {
      return;
    }
    setIsError(false);
    if (!isLastPage || refresh) {
      if (refresh) {
        setData([]);
      }
      setIsLoading(true);
      const result = await getPatients(refresh ? 1 : page);
      if (!result) {
        setIsError(true);
        setIsLastPage(true);
        setIsLoading(false);
        return;
      }
      setData(prev => {
        if (refresh) {
          return [...result?.data!];
        } else {
          return [...prev, ...result?.data!].filter((value, index, self) => {
            return (
              self.findIndex(obj =>
                //@ts-ignore
                ['id'].every(key => obj[key] === value[key]),
              ) === index
            );
          });
        }
      });
      setPage(prev => (refresh ? 2 : prev + 1));
      const pageSize = 20;
      const totalPageCount = Math.ceil(
        parseFloat(result?.total ? result?.total + '' : '2') / pageSize,
      );
      setIsLastPage((refresh ? 1 : page) >= totalPageCount);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMore(true);
  }, []);

  // Web scroll handler that manages both animation and pagination
  const webScrollHandler = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    // Calculate if we need to load more content
    const {contentOffset, contentSize, layoutMeasurement} = event.nativeEvent;
    // Calculate scroll percentage - how far down the user has scrolled
    const scrollPercentage =
      (contentOffset.y + layoutMeasurement.height) / contentSize.height;

    // If we've scrolled past the threshold, load more content
    if (scrollPercentage >= loadMoreThreshold && !isLoading && !isLastPage) {
      loadMore();
    }
  };

  // Function to handle viewing patient details
  const handleViewPatient = (patient: PatientType) => {
    navigation.navigate(screens.content.viewPatient, {patient});
  };

  // Function to handle editing patient
  const handleEditPatient = (patient: PatientType) => {
    navigation.navigate(screens.content.addEditPatient, {
      patient,
      callback: () => loadMore(true),
    });
  };

  // Function to handle deleting patient
  const handleDeletePatient = (patient: PatientType) => {
    openQuestionModal({
      title: `Delete ${patient.name || 'Patient'} Record`,
      message:
        "Are you sure you want to delete this patient's record? This action cannot be undone.",
      type: 'question',
      onConfirm: async () => {
        const res = await deletePatient(patient?.id);
        if (res) {
          const tempData = data.filter(i => i.id !== patient.id);
          setData(tempData);
        }
      },
    });
  };

  // Column definitions
  const columns = [
    {id: 'name', label: 'NAME', width: '25%'},
    {id: 'dob', label: 'DATE OF BIRTH', width: '25%'},
    {id: 'gender', label: 'GENDER', width: '15%'},
    {id: 'phone', label: 'CONTACT', width: '20%'},
    {id: 'actions', label: 'ACTIONS', width: '15%'},
  ];

  // Render table header
  const renderTableHeader = () => (
    <TableHeader>
      {columns.map(column => (
        <TableHeaderCell key={column.id} width={column.width}>
          <TableHeaderText bold>{column.label}</TableHeaderText>
        </TableHeaderCell>
      ))}
    </TableHeader>
  );

  // Render table rows
  const renderPatientRows = () =>
    data.map(patient => (
      <TableRow key={patient.id}>
        <TableCell width={columns[0].width}>
          <CellText>{patient.name}</CellText>
        </TableCell>
        <TableCell width={columns[1].width}>
          <CellText>{dayjs(patient.dob).format('YYYY-MM-DD')}</CellText>
        </TableCell>
        <TableCell width={columns[2].width}>
          <CellText>{patient.gender}</CellText>
        </TableCell>
        <TableCell width={columns[3].width}>
          <CellText>{patient.phone}</CellText>
        </TableCell>
        <TableCell width={columns[4].width}>
          <ActionButtonsContainer>
            <IconContainer onPress={() => handleViewPatient(patient)}>
              <Icon
                name="eyeOutline"
                size={theme.text.s6}
                color={theme.settings.icon}
              />
            </IconContainer>
            <IconContainer onPress={() => handleEditPatient(patient)}>
              <Icon
                name="edit"
                size={theme.text.s6}
                color={theme.settings.icon}
              />
            </IconContainer>
            <IconContainer
              style={{backgroundColor: theme.text.error + '11'}}
              onPress={() => handleDeletePatient(patient)}>
              <Icon
                name="delete"
                size={theme.text.s6}
                color={theme.text.error}
              />
            </IconContainer>
          </ActionButtonsContainer>
        </TableCell>
      </TableRow>
    ));

  // Render patient cards for mobile view
  const renderPatientCards = () =>
    data.map(patient => (
      <PatientCard key={patient.id}>
        <CardRow>
          <CardLabel bold>Name:</CardLabel>
          <CardValue>{patient.name}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel bold>DOB:</CardLabel>
          <CardValue>{patient.dob?.split('T')[0] || '-'}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel bold>Gender:</CardLabel>
          <CardValue>{patient.gender}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel bold>Contact:</CardLabel>
          <CardValue>{patient.phone}</CardValue>
        </CardRow>
        <ActionButtonsContainer
          style={{justifyContent: 'flex-end', marginTop: 16}}>
          <IconContainer onPress={() => handleViewPatient(patient)}>
            <Icon
              name="eyeOutline"
              size={theme.text.s6}
              color={theme.settings.icon}
            />
          </IconContainer>
          <IconContainer onPress={() => handleEditPatient(patient)}>
            <Icon
              name="edit"
              size={theme.text.s6}
              color={theme.settings.icon}
            />
          </IconContainer>
          <IconContainer
            style={{backgroundColor: theme.text.error + '11'}}
            onPress={() => handleDeletePatient(patient)}>
            <Icon name="delete" size={theme.text.s6} color={theme.text.error} />
          </IconContainer>
        </ActionButtonsContainer>
      </PatientCard>
    ));

  return (
    <MainLayout
      enableScroll={false}
      title={tr('drawer.patients')}
      backHeader
      menuHeader
      noPadding
      noPaddingHeader>
      <ScrollView onScroll={webScrollHandler}>
        <Container headerHeight={headerHeight}>
          <ContentContainer>
            <HeaderContainer>
              <Title bold>{tr('patients.title')}</Title>
              <AddButton
                onPress={() =>
                  navigation.navigate(screens.content.addEditPatient, {
                    callback: () => loadMore(true),
                  })
                }
                label={tr('patients.addPatient')}
              />
            </HeaderContainer>
            {isMobile ? (
              <CardContainer>
                <TableBody>
                  {isLoading && data.length === 0 ? (
                    <SearchListSkeleton
                      numberOfItems={10}
                      height={theme.text.s1 * 5}
                    />
                  ) : (
                    renderPatientCards()
                  )}
                </TableBody>
              </CardContainer>
            ) : (
              <TableContainer>
                {renderTableHeader()}
                <TableBody>
                  {isLoading && data.length === 0 ? (
                    <TableRowSkeleton />
                  ) : (
                    renderPatientRows()
                  )}

                  {isLoading && data.length !== 0 && (
                    <View style={{padding: theme.text.s1}}>
                      <Loader />
                    </View>
                  )}
                  {(isError || data.length === 0) && !isLoading && (
                    <EmptyContainer>
                      <EmptyList
                        iconName={'empty'}
                        iconColor={theme.primary}
                        iconSize={theme.text.s1 * 2}
                        title={isError ? 'app.error' : 'app.empty'}
                        onRefreshPress={() => loadMore(true)}
                        showRefresh={isError}
                      />
                    </EmptyContainer>
                  )}
                </TableBody>
              </TableContainer>
            )}
          </ContentContainer>
        </Container>
      </ScrollView>
    </MainLayout>
  );
};

Patients.displayName = 'PatientsScreen';
export default Patients;
